import { call, select, spawn } from 'typed-redux-saga'
import { put } from 'redux-saga/effects'

import { logger } from 'APP/Lib/Datadog'
import { fetchNewPostsRequestHelper, isValidPostData } from 'APP/Lib/MessagingHelpers'
import { selectAccessToken } from 'APP/Redux/LoginRedux'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import { setIsConnectedIfDisconnected } from 'APP/Sagas/AppSessionSagas'
import Mattermost from 'APP/Services/Mattermost'
import { selectHistoryState } from 'APP/Store/History/selectors'
import type {
  FetchAllPostsForChannelPayload,
  FetchNewPostsForChannelPayload,
  FetchOlderPostsForChannelPayload,
} from 'APP/Store/History/types'

import { POSTS_PER_PAGE } from './constants'
import { handleNewMessage } from './utils'

export function* fetchNewPostsForChannel({ channelId }: FetchNewPostsForChannelPayload) {
  try {
    if (!channelId) return

    const history = yield* select(selectHistoryState)
    const accessToken = yield* select(selectAccessToken)

    const mmClient = Mattermost.create(accessToken)
    const { channels } = history
    const channel = channels[channelId]

    const postData = yield* call(fetchNewPostsRequestHelper, {
      channelId,
      mmClient,
      channel,
    })

    if ('error' in postData) throw postData.error

    yield put(PatientHistoryActions.fetchPostsSuccess(channelId, postData, undefined))

    const lastPost = isValidPostData(postData) && postData.posts[postData.order[0]]
    if (lastPost) {
      yield* call(handleNewMessage, lastPost)
    }
  } catch (error) {
    logger.debug('fetchNewPostsForChannel saga error', { channelId }, error)
    yield put(PatientHistoryActions.fetchPostsFailure(channelId, error))
  }
}

export function* fetchOlderPostsForChannel({ channelId }: FetchOlderPostsForChannelPayload) {
  try {
    if (!channelId) return

    const history = yield* select(selectHistoryState)
    const accessToken = yield* select(selectAccessToken)

    const mmClient = Mattermost.create(accessToken)
    const { oldestFetchedPostId } = history.channels[channelId]
    if (!oldestFetchedPostId) return
    const postData = yield* call(
      mmClient.getPostsBeforePostId,
      channelId,
      oldestFetchedPostId,
      POSTS_PER_PAGE
    )

    if (postData?.order?.length === 0) {
      yield put(PatientHistoryActions.setAllOlderPostsFetched(channelId, true))
    }

    yield put(PatientHistoryActions.fetchPostsSuccess(channelId, postData, undefined))
  } catch (error) {
    logger.debug('fetchOlderPostsForChannel saga error', { channelId }, error)
    yield put(PatientHistoryActions.fetchPostsFailure(channelId, error))
  }
}

export function* fetchAllPostsForChannel({
  channelId,
  limit = 1000,
}: FetchAllPostsForChannelPayload) {
  try {
    if (!channelId) return

    const accessToken = yield* select(selectAccessToken)

    const mmClient = Mattermost.create(accessToken)
    const postData = yield* call(mmClient.getPosts, channelId, { limit })

    yield* spawn(setIsConnectedIfDisconnected)

    yield put(PatientHistoryActions.fetchPostsSuccess(channelId, postData, true))
  } catch (error) {
    logger.debug('fetchAllPostsForChannel saga error', { channelId, limit }, error)
    yield put(PatientHistoryActions.fetchPostsFailure(channelId, error))
  }
}
